import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Slider } from "../components/ui/slider"

export function SliderRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Legacy pre-v2 slider blocks overload `element.variant`/`element.size` as
  // numeric min/max carriers; those semantics are preserved unchanged below
  // and remain separate from the theme-v2 data-variant/data-size surface.
  const atomDefaults = useAtomDefaults("slider")
  const resolvedVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedSize = atomDefaults.defaultSize ?? "default"

  const rawMin = element.min != null ? Number(element.min) : (element.variant ? Number(resolveSymbol(element.variant) ?? 0) : 0)
  const rawMax = element.max != null ? Number(element.max) : (element.size ? Number(resolveSymbol(element.size) ?? 100) : 100)
  const min = Number.isFinite(rawMin) ? rawMin : 0
  const max = Number.isFinite(rawMax) ? rawMax : 100
  const rawStep = element.step != null ? Number(element.step) : 1
  const authoredStep = Number.isFinite(rawStep) ? rawStep : 1
  const step = authoredStep > 0 ? authoredStep : 1
  const hasUsableRange = max > min
  const interactionMax = hasUsableRange ? max : min + step
  const rawDefault = element.value != null
    ? Number(element.value)
    : element.defaultValue != null
      ? Number(element.defaultValue)
      : min
  const finiteDefault = Number.isFinite(rawDefault) ? rawDefault : min
  const defaultVal = Math.min(interactionMax, Math.max(min, finiteDefault))
  const coerceNumber = (raw: unknown) => {
    const numeric = Number(raw)
    return Number.isFinite(numeric)
      ? Math.min(interactionMax, Math.max(min, numeric))
      : defaultVal
  }

  const [value, setValue] = useBoundValue<number>(
    element.bind,
    resolveSymbol,
    defaultVal,
    coerceNumber,
  )

  const handleValueChange = (vals: number | readonly number[]) => {
    const first = Array.isArray(vals) ? (vals as readonly number[])[0] : (vals as number)
    if (element.bind !== undefined) setValue(first)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: first })
  }

  return (
    <div
      className={element.className}
      data-testid={`slider-${element.id}`}
      data-action="input"
      data-field-type="slider"
      data-atom-type="slider"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-min={min}
      data-max={max}
      data-step={authoredStep}
      data-value={value}
    >
      <Slider
        id={element.id}
        min={min}
        max={interactionMax}
        step={step}
        disabled={!hasUsableRange || element.disabled === true}
        {...(element.bind !== undefined
          ? { value: [value], onValueChange: handleValueChange }
          : { defaultValue: [defaultVal], onValueChange: handleValueChange })}
      />
    </div>
  )
}
